import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Container,
  Card,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaSave,
  FaPlus,
  FaFacebook,
  FaInstagram,
  FaShareAlt,
  FaInfoCircle,
  FaEdit,
} from "react-icons/fa";

const API_URL = "https://radheyelectrical.com/api/scoial_api.php";

const CategoryAdmin = () => {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState("social_link");

  const defaultForm = {
    id: "",
    badge: "",
    title: "",
    subtitle: "",
    image_url: "",
    sort_order: 0,
    category: "social_link",
  };

  const [formData, setFormData] = useState(defaultForm);

  // --- FETCH ---
  const fetchItems = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();

      if (Array.isArray(data)) {
        const normalized = data.map((item) => ({
          ...item,
          category:
            item.category === "contact_info" ? "contact_info" : "social_link",
        }));
        setItems(normalized);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // --- SAVE ---
  const handleSave = async () => {
    const method = formData.id ? "PUT" : "POST";

    let payload = { ...formData };

    // 🔒 Preserve category on update
    if (formData.id) {
      const original = items.find((i) => i.id === formData.id);
      if (original) {
        payload.category =
          original.category === "contact_info" ? "contact_info" : "social_link";
      }
    }

    try {
      const res = await fetch(API_URL, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setShow(false);
        fetchItems();
      }
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  // --- MODAL ---
  const openModal = (item = defaultForm) => {
    setFormData({
      ...item,
      category: item.category === "contact_info" ? "contact_info" : activeTab,
    });
    setShow(true);
  };

  // --- FILTERED DATA ---
  const filteredItems = items
    .filter((i) => i.category === activeTab)
    .sort((a, b) => a.sort_order - b.sort_order);

  // --- ICON ---
  const getIcon = (item) => {
    if (item.category === "social_link") {
      const t = (item.title || "").toLowerCase();
      if (t.includes("facebook"))
        return <FaFacebook className="me-2 text-primary" />;
      if (t.includes("instagram"))
        return <FaInstagram className="me-2 text-danger" />;
      return <FaShareAlt className="me-2 text-primary" />;
    }
    return <FaInfoCircle className="me-2 text-info" />;
  };

  return (
    <Container className="mt-4">
      <Card className="shadow-sm border-0">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <FaShareAlt className="me-2 text-primary" />
            Social & Contact Management
          </h4>
          <Button variant="success" onClick={() => openModal()}>
            <FaPlus className="me-2" /> Add New
          </Button>
        </Card.Header>

        <Card.Body>
          {/* ✅ TABS */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="social_link" title="Social Links" />
            <Tab eventKey="contact_info" title="Contact Info" />
          </Tabs>

          {/* ✅ TABLE */}
          <Table responsive hover>
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Title</th>
                <th>Subtitle</th>
                <th>Link</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.sort_order}</td>
                  <td>
                    {getIcon(item)}
                    {(item.category || "").replace("_", " ")}
                  </td>
                  <td>{item.title}</td>
                  <td>{item.subtitle}</td>
                  <td>{item.image_url}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => openModal(item)}
                    >
                      <FaEdit />
                    </Button>
                  </td>
                </tr>
              ))}

              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ✅ MODAL */}
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? "Update Entry" : "Create Entry"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {formData.id !== "Update Entry" && (
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={formData.category}
                  disabled={!!formData.id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value,
                    })
                  }
                >
                  <option value="social_link">Social Link</option>
                  <option value="contact_info">Contact Info</option>
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                type="text"
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, subtitle: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Links / URL</Form.Label>
              <Form.Control
                type="text"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData({ ...formData, image_url: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sort_order: Number(e.target.value),
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" /> Save Data
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CategoryAdmin;
